import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronDown, Settings, User } from "lucide-react";
import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { AppShellTranslations } from "@/components/app/i18n/shell";
import type { SidebarClasses } from "./sidebar-theme";
import { getInitials } from "./sidebar-utils";

export interface SidebarUserInfo {
  userName: string;
  userEmail: string;
  userAvatar: string;
  onSignOut: () => void;
}

interface SidebarUserMenuProps extends SidebarUserInfo {
  roleLabel: string;
  canViewSettings: boolean;
  collapsed: boolean;
  classes: SidebarClasses;
  labels: AppShellTranslations["sidebar"];
}

const ITEM_CLASS = "h-[34px] gap-2.5 rounded-[6px] px-2.5 text-[13px] font-medium leading-[17px] focus:bg-tr-canvas";

/**
 * Menu do usuário (figma-spec §5, S11). `aria-label` genérico (C3); nome e e-mail só como conteúdo visível.
 * `Sair` chama o mesmo `onSignOut` do AppLayout que o header usava (C2).
 */
export function SidebarUserMenu(props: SidebarUserMenuProps) {
  const { userName, userEmail, userAvatar, roleLabel, canViewSettings, onSignOut, collapsed, classes, labels } = props;
  const avatar = (size: string) => (
    <Avatar className={cn("shrink-0", size)}>
      {userAvatar && <AvatarImage src={userAvatar} alt="" />}
      <AvatarFallback className={cn("text-[12px] font-bold", classes.userAvatar)}>
        {getInitials(userName)}
      </AvatarFallback>
    </Avatar>
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={labels.userMenu}
        className={cn(
          "flex items-center outline-none transition-colors focus-visible:ring-2",
          classes.ring,
          classes.hover,
          collapsed
            ? "h-10 w-12 justify-center rounded-[8px]"
            : "w-full gap-2.5 rounded-[10px] py-2 pl-2 pr-2.5 text-left",
        )}
      >
        {collapsed ? (
          avatar("h-9 w-9")
        ) : (
          <>
            {avatar("h-8 w-8")}
            <span className="min-w-0 flex-1">
              <span className={cn("block truncate text-[13px] font-semibold leading-[17px]", classes.strong)}>
                {userName}
              </span>
              {roleLabel && (
                <span className={cn("block truncate text-[11px] leading-[14px]", classes.sub)}>{roleLabel}</span>
              )}
            </span>
            <ChevronDown className={cn("h-4 w-4 shrink-0", classes.icon)} strokeWidth={1.75} aria-hidden="true" />
          </>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="top"
        align="start"
        sideOffset={6}
        className="w-[248px] rounded-[12px] border-tr-border bg-white p-1.5 font-dm shadow-[0_12px_32px_rgba(10,26,51,0.18)]"
      >
        <DropdownMenuLabel className="flex flex-col items-start gap-0.5 px-2.5 pb-2.5 pt-2 font-normal">
          <span className="w-full truncate text-[13.5px] font-semibold leading-[18px] text-tr-ink">{userName}</span>
          {userEmail && <span className="w-full truncate text-[12px] leading-[16px] text-tr-muted">{userEmail}</span>}
          {roleLabel && (
            <span className="mt-1 rounded-[4px] bg-tr-navy/[0.07] px-1.5 py-0.5 text-[10.5px] font-semibold leading-[14px] text-tr-navy">
              {roleLabel}
            </span>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="mx-0 bg-tr-border" />
        <DropdownMenuItem asChild className={cn(ITEM_CLASS, "text-tr-ink")}>
          <Link to="/app/profile">
            <User className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" />
            {labels.profile}
          </Link>
        </DropdownMenuItem>
        {canViewSettings && (
          <DropdownMenuItem asChild className={cn(ITEM_CLASS, "text-tr-ink")}>
            <Link to="/app/settings">
              <Settings className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" />
              {labels.orgSettings}
            </Link>
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator className="mx-0 bg-tr-border" />
        <DropdownMenuItem onSelect={onSignOut} className={cn(ITEM_CLASS, "text-tr-risk-high focus:text-tr-risk-high")}>
          <ArrowUpRight className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" />
          {labels.logout}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
